import React from "react"
import { useTranslation } from "react-i18next"

import * as TeamApi from "features/api/TeamApi"
import * as PartnerApi from "features/Partner/api/PartnerApi"

import { useAlert, useTheme, useAuth } from "hooks"

import FormElement from "components/Form/FormElement"
import { userPermissionList, BC_OPERATOR } from "utils/constants"
import { jsonHash } from "utils/hash"
import { getEntityId } from "utils/user"

const defaultFormEvents = {
    ready: true
}

export const FormProfil = ({
    profil,
    user,
    slug,
    permissionList = userPermissionList,
    onFormProfilSuccess
}) => {
    const defaultFormFields = React.useMemo(() => ({
        userid: profil?.userid || null,
        rowversion: profil?.rowversion || null,
        role: parseInt(profil?.role) || 1,
        name: profil?.name || "",
        firstname: profil?.firstname || "",
        mail: profil?.mail || "",
        confirmMail: "",
        // newEmail: "",
        // code: ""
    }), [profil])

    const [formFields, setFormFields] = React.useState(defaultFormFields)
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const [permission, setPermission] = React.useState(1)

    const [errors, setErrors] = React.useState([])
    const [showLocalErrors, setShowLocalErrors] = React.useState(false)

    const formFieldHash = React.useRef(jsonHash(defaultFormFields))

    const { alert } = useAlert()
    const { currentTheme } = useTheme()
    const { t } = useTranslation()
    const { logout } = useAuth()

    React.useEffect(() => {
        setFormFields(profil)
        setPermission(user?.permission)
    }, [profil]) // eslint-disable-line

    const handleInputChange = (event) => {
        const target = event.target
        const value = target.type === "checkbox" ? target.checked : target.value
        const name = target.name

        setFormFields({
            ...formFields,
            [name]: value
        })
    }

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0) && !(formFieldHash.current === jsonHash(formFields))

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            try {
                // hide error
                setShowLocalErrors(false)
                // prevent form to be submitted again
                setFormEvents({
                    ...formEvents,
                    ready: false
                })

                const profil = {
                    description: {
                        nom: formFields.name,
                        prenom: formFields.firstname
                    },
                    permission: parseInt(formFields.role)
                }

                if (formFields.userid !== undefined) {
                    profil.user_id = formFields.userid
                    profil.row_version = formFields.rowversion
                    if (profil.user_id === user?.user_id) {
                        delete profil.permission
                    }

                    const { getError } = await TeamApi.putProfil(profil)
                    if (getError()) throw getError()
                    alert("success", t("edit_success", { ns: "team" }))
                } else {
                    profil.entity_id = slug ? slug : getEntityId()
                    profil.status = 0
                    profil.mail = formFields.mail.trim()
                    if (slug) {
                        const { res: loadedPartner, getError } = await PartnerApi.getPartner(slug)
                        if (getError()) throw getError()
                        profil.registermailtemplate = loadedPartner?.data_operators[0].operator
                    } else {
                        profil.registermailtemplate = (currentTheme?.id_api === "OC") ? BC_OPERATOR : currentTheme.id_api
                    }

                    const { getError } = await TeamApi.postProfil(profil)
                    if (getError()) throw getError()

                    alert("success", t("add_success", { ns: "team" }))
                }


                if (onFormProfilSuccess) onFormProfilSuccess()
            } catch (error) {
                console.error(error)
                const erroMsg = error?.message?.toString()
                if (erroMsg) alert("error", erroMsg)

                if (["Access token is required."].includes(error?.error?.message)) logout()
            } finally {
                setFormEvents(s => ({
                    ...s,
                    ready: true
                }))
            }
        }
    }

    return (
        <form className="">
            <section className="mb-5">
                <h2 className="c-h2 text-primary font-bold uppercase mb-5">
                    {profil.userid ? t("edit_title", { ns: "team" }) : t("add", { ns: "team" })}
                </h2>
                <div className="flex flex-wrap">
                    <div className="w-full py-3">
                        {/* Role */}
                        <FormElement
                            value={formFields.role}
                            name="role"
                            type="radio"
                            label={t("profil.role", { ns: "team" })}
                            options={
                                permission === 1 && formFields.role === 1
                                    ? permissionList.filter(oneRole => oneRole.value === 1)
                                    : permissionList.filter(oneRole => oneRole.value !== -1)
                            }
                            className="mb-5"
                            classNameOptionsContainer="flex-col"
                            classNameLabel="mb-5"
                            classNameOption="flex-1 items-center"
                            required={true}
                            readonly={permission === 0 && profil.userid === user?.user_id}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="w-full py-3">
                        {/* Name */}
                        <FormElement
                            value={formFields.name}
                            name="name"
                            type="text"
                            label={t("profil.name", { ns: "team" })}
                            className="mb-5"
                            required={true}
                            readonly={permission === 1}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="w-full py-3">
                        {/* Firstname */}
                        <FormElement
                            value={formFields.firstname}
                            name="firstname"
                            type="text"
                            label={t("profil.firstname", { ns: "team" })}
                            className="mb-5"
                            required={true}
                            readonly={permission === 1}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="w-full py-3">
                        {/* Mail */}
                        <FormElement
                            value={formFields.mail}
                            name="mail"
                            type="email"
                            label={t("profil.mail", { ns: "team" })}
                            className="mb-5"
                            required={true}
                            readonly={defaultFormFields.mail ? true : false}
                            disabled={defaultFormFields.mail ? true : false}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />
                    </div>

                    {formFields.userid === undefined &&
                        <div className="w-full py-3">
                            {/* Confirm Mail */}
                            <FormElement
                                value={formFields.confirmMail}
                                mailToConfirm={formFields.mail}
                                name="confirmMail"
                                type="email"
                                label={t("profil.confirm_mail", { ns: "team" })}
                                className="mb-5"
                                required={true}
                                showErrors={showLocalErrors}
                                onValidate={handleValidation}
                                onChange={handleInputChange}
                            />
                        </div>
                    }
                </div>
            </section>
            <div className="flex justify-end flex-col md:flex-row-reverse">
                {formEvents.ready &&
                    <button
                        type="submit"
                        className="c-btn c-btn--primary px-12 mb-5 lg:m-0"
                        disabled={errors.length > 0}
                        onClick={handleSubmit}
                    >
                        {t("global.validate")}
                    </button>
                }
                {!formEvents.ready &&
                    <div className="c-spinner"></div>
                }
            </div>
        </form>
    )
}
